import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Lightbulb } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { WorldZone } from '../shared/WorldZone';
import { Card } from '../ui/Card';
import { itemVariants } from '../shared/animations';
import { MODULES } from '../../content';
import { getModuleFromQuestionId } from '../../utils/activityHelpers';
import { FAMILY_DISPLAY_NAMES } from '../../portals/teacher-v2/utils/familyDisplayNames';
interface ErrorPatternsSectionProps {
  state: AppState;
  selectedGroup: GroupId;
}
// Adapted from differentiationInsights.ts for learner view
const ERROR_TYPE_RECOMMENDATIONS: Record<string, string> = {
  // Clock errors
  HOUR_OFF: 'Let goed op de kleine wijzer. Welk uur is het net geweest?',
  MINUTE_OFF_5: 'Kijk nog eens goed naar de grote wijzer. Tel in sprongetjes van 5!',
  MINUTE_OFF_10: 'Kijk nog eens goed naar de grote wijzer. Tel in sprongetjes van 5!',
  MINUTE_OFF_15: 'Verwar je kwart voor en kwart over? Kwart over is op de 3, kwart voor is op de 9.',
  HALF_CONFUSION: 'Half betekent dat de grote wijzer op de 6 staat, precies tussen twee uren in.',
  FORMAT_24H: "Let op of het overdag (12-uurs) of 's middags/'s avonds (24-uurs) is.",
  BOTH_OFF: 'Neem de tijd om eerst naar het uur te kijken, en dan pas naar de minuten.',
  // Money errors
  TOO_LOW: 'Je bedrag is een beetje te laag. Heb je alle munten en biljetten geteld?',
  TOO_HIGH: 'Je bedrag is een beetje te hoog. Probeer gepast te betalen!',
  TOO_LOW_5C: 'Vergeet je niet een muntje van 5 cent toe te voegen?',
  TOO_HIGH_5C: 'Heb je misschien een muntje van 5 cent te veel gebruikt?',
  TOO_LOW_10C: 'Vergeet je niet een muntje van 10 cent toe te voegen?',
  TOO_HIGH_10C: 'Heb je misschien een muntje van 10 cent te veel gebruikt?',
  TOO_LOW_50C: 'Vergeet je niet een muntje van 50 cent toe te voegen?',
  TOO_HIGH_50C: 'Heb je misschien een muntje van 50 cent te veel gebruikt?',
  TOO_LOW_100C: 'Vergeet je niet een hele euro toe te voegen?',
  TOO_HIGH_100C: 'Heb je misschien een euro te veel gebruikt?',
  INVALID_DENOM: 'Gebruik alleen de munten en biljetten die je mag gebruiken in deze opdracht.',
  TOO_FEW_TOKENS: 'Je hebt te weinig munten of biljetten gebruikt.',
  TOO_MANY_TOKENS: 'Probeer met zo min mogelijk munten en biljetten te betalen!',
  MISSING_REQUIRED_BILL: 'Vergeet niet het verplichte biljet te gebruiken.',
  // Spelling/Grammar
  STAM_T: 'Denk aan de smurfenregel! Ik smurf, hij smurft. Hoor je een t? Dan schrijf je stam+t.',
  D_T: 'Maak het woord langer om te horen of het op een d of t eindigt (hond - honden).',
  EI_IJ: 'Twijfel je tussen ei en ij? Denk aan het ei-verhaal!',
  AU_OU: 'Twijfel je tussen au en ou? Denk aan het au-verhaal!'
};
function humanizeFamilyId(familyId: string): string {
  const parts = familyId.split('-').slice(1);
  return parts.join(' ').toLowerCase().replace(/^\w/, c => c.toUpperCase());
}
export function ErrorPatternsSection({
  state,
  selectedGroup
}: ErrorPatternsSectionProps) {
  const errorPatterns = useMemo(() => {
    if (!state.familyMastery) return [];
    const groupSuffix = `_g${selectedGroup}`;
    const errorCounts: Record<string, {
      count: number;
      familyId: string;
    }> = {};
    // 1. Iterate all familyMastery entries matching group
    Object.entries(state.familyMastery).forEach(([key, familyState]) => {
      if (!key.endsWith(groupSuffix) || !familyState.history) return;
      const familyId = familyState.familyId;
      // 2. Count errorType occurrences in incorrect attempts
      familyState.history.forEach(attempt => {
        if (!attempt.isCorrect && attempt.errorType) {
          const errorType = attempt.errorType;
          if (!errorCounts[errorType]) {
            errorCounts[errorType] = {
              count: 0,
              familyId
            };
          }
          errorCounts[errorType].count++;
          // Keep the familyId of the most recent error for this type
          errorCounts[errorType].familyId = familyId;
        }
      });
    });
    // 3. Find top 3 most common error types (min 2 occurrences)
    return Object.entries(errorCounts).filter(([_, data]) => data.count >= 2).sort((a, b) => b[1].count - a[1].count).slice(0, 3).map(([errorType, data]) => {
      const baseErrorType = errorType.replace(/_\d+$/, '');
      let recommendation = 'Neem even de tijd om de uitleg goed te lezen als je een foutje maakt.';
      if (ERROR_TYPE_RECOMMENDATIONS[errorType]) {
        recommendation = ERROR_TYPE_RECOMMENDATIONS[errorType];
      } else if (ERROR_TYPE_RECOMMENDATIONS[baseErrorType]) {
        recommendation = ERROR_TYPE_RECOMMENDATIONS[baseErrorType];
      }
      const moduleId = getModuleFromQuestionId(data.familyId);
      const moduleDef = MODULES.find(m => m.id === moduleId);
      const familyName = FAMILY_DISPLAY_NAMES[data.familyId] || humanizeFamilyId(data.familyId);
      return {
        errorType,
        count: data.count,
        familyId: data.familyId,
        familyName,
        moduleIcon: moduleDef?.icon || '💡',
        moduleColor: moduleDef?.color || 'bg-violet-500',
        recommendation
      };
    });
  }, [state.familyMastery, selectedGroup]);
  return <WorldZone icon={Lightbulb} title="Slimme Leertips" accentColor="bg-violet-100 text-violet-600" data-id="element-1618">
      {errorPatterns.length === 0 ? <Card className="p-6 text-center bg-slate-50 border-dashed border-2 border-slate-200 rounded-[2rem]" data-id="element-1619">
          <p className="text-slate-500 font-medium" data-id="element-1620">
            Geen specifieke tips — je doet het geweldig! 🌟
          </p>
        </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1621">
          {errorPatterns.map((pattern, i) => <motion.div key={`${pattern.errorType}-${i}`} variants={itemVariants} data-id="element-1622">
              <Card className="p-5 h-full flex flex-col border-2 border-violet-100 bg-white rounded-[1.5rem] shadow-sm hover:shadow-md transition-shadow" data-id="element-1623">
                <div className="flex items-center gap-3 mb-3" data-id="element-1624">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm ${pattern.moduleColor.replace('bg-', 'bg-opacity-20 text-').replace('500', '700')} bg-opacity-20`} data-id="element-1625">
                    {pattern.moduleIcon}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1626">
                    <h4 className="font-bold text-slate-800 truncate" data-id="element-1627">
                      {pattern.familyName}
                    </h4>
                    <p className="text-xs text-slate-500 font-medium" data-id="element-1628">
                      Tip voor jou
                    </p>
                  </div>
                </div>
                <div className="bg-violet-50 rounded-xl p-4 flex-1" data-id="element-1629">
                  <p className="text-violet-800 text-sm font-medium leading-relaxed" data-id="element-1630">
                    {pattern.recommendation}
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </div>}
    </WorldZone>;
}